import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING =
  "Hey — I'm the FIS assistant. Ask me about recruitment, software, chatbots or web work we take on.";

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: GREETING },
  ]);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("fis:open-chatbot", handler);
    return () => window.removeEventListener("fis:open-chatbot", handler);
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
      });
      setSessionId(res.data.session_id);
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.data.reply },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry, I couldn't reach the server. Try again in a moment, or drop a note via the contact form.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    "What do you build?",
    "Can you hire for us?",
    "Do you offer AI chatbots?",
    "How fast can you start?",
  ];

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-trigger-button"
          className="fixed bottom-6 right-6 z-50 fis-chat-pulse bg-[color:var(--fis-accent)] text-white rounded-full w-14 h-14 grid place-items-center shadow-lg hover:scale-105 transition-transform"
          aria-label="Open chat"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="chatbot-panel"
            className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[400px] h-[78vh] max-h-[600px] bg-white border fis-hairline shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b fis-hairline bg-[color:var(--fis-ink)] text-white">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-[color:var(--fis-accent)] rounded-full grid place-items-center">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-display text-sm">FIS Assistant</div>
                  <div className="font-mono-label text-[0.6rem] text-white/60">
                    Powered by GPT-4o-mini
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chatbot-close"
                aria-label="Close chat"
                className="p-1 hover:bg-white/10 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto p-5 space-y-4 bg-[color:var(--fis-bg-alt)]"
              data-testid="chatbot-messages"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${
                    m.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    data-testid={`chat-msg-${m.role}-${i}`}
                    className={`max-w-[82%] px-4 py-3 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[color:var(--fis-ink)] text-white"
                        : "bg-white border fis-hairline"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border fis-hairline px-4 py-3 text-sm">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-[color:var(--fis-ink)] rounded-full animate-bounce" />
                      <span
                        className="w-1.5 h-1.5 bg-[color:var(--fis-ink)] rounded-full animate-bounce"
                        style={{ animationDelay: "0.15s" }}
                      />
                      <span
                        className="w-1.5 h-1.5 bg-[color:var(--fis-ink)] rounded-full animate-bounce"
                        style={{ animationDelay: "0.3s" }}
                      />
                    </span>
                  </div>
                </div>
              )}
            </div>

            {messages.length <= 1 && (
              <div className="px-5 py-3 border-t fis-hairline flex flex-wrap gap-2 bg-white">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    data-testid={`chat-suggestion-${s.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
                    className="text-xs px-3 py-1.5 border fis-hairline hover:border-[color:var(--fis-accent)] hover:text-[color:var(--fis-accent)]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <form
              onSubmit={send}
              className="flex items-center gap-2 p-4 border-t fis-hairline bg-white"
              data-testid="chatbot-form"
            >
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask FIS anything…"
                className="flex-1 fis-input !py-2 !border-b"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="chatbot-send"
                className="w-10 h-10 bg-[color:var(--fis-accent)] text-white grid place-items-center disabled:opacity-50 hover:bg-[#0024a0] transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
